import React, {useEffect, useState} from 'react';
import Swal from "sweetalert2";
import {useQuery} from "@tanstack/react-query";
import {useParams} from "react-router";
import configObjectService from "../../../../services/web/config-object-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import AlertMessages from "../../../../components/messages/alert-message.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {v4 as uuidv4} from "uuid";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaInfoCircle, FaPlus, FaTrash} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";


const Measure = [
    {value: "MILLIGRAM", label: "Miligrama (mg)"},
    {value: "GRAM", label: "Grama (g)"},
    {value: "KILOGRAM", label: "Quilograma (kg)"},
    {value: "MILLILITER", label: "Milimetro (mL)"},
    {value: "LITER", label: "Litro (L)"},
    {value: "CENTIMETER", label: "Centímetro (cm)"},
    {value: "METER", label: "Metro (m)"},
    {value: "PIECE", label: "Peça (pc)"},
    {value: "UNITY", label: "Unidade (un)"},
];
const ConfigObjectForm = () => {
    const navigate = navigateHook();

    const {id} = useParams()
    const [state, setState] = useState([{name: "", mandatory_quantity: false, mandatory_subtype: false}]);
    const [subtypeState, setSubtypeState] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [coin, setCoin] = useState(false)
    const [isSystem, setIsSystem] = useState(false)
    const onChange = ({target: {value, id}}) => handleInputs(value, id)

    const handleInputs = (value, id) => {
        setState(prev => ({...prev, [id]: value}));
    }

    const handleCheckBox = (element) => {
        let {id, checked} = element.target;
        handleInputs(checked, id);
    }

    const {refetch, fetchStatus} = useQuery({
        queryFn: () => configObjectService.get(id).then((data) => {
            data.subtype.map((sbt) => {
                const units = JSON.parse(sbt.units)

                if (units){
                    sbt.measure = units.map((measure) => {
                        return Measure.find(item => item.value === measure)
                    })
                }else{
                    sbt.measure = []
                }

                return sbt
            })
            if (data.name == "Moeda") setCoin(true)
            setIsSystem(data.system)
            data.subtype.length > 0 ? setSubtypeState(data.subtype) : setSubtypeState([])
            setState(data)
            return [];
        }),
        queryKey: ['request', {id}],
        enabled: !!id,
    });

    useEffect(() => {
        if (id){
            if (fetchStatus == "pending") {
                setIsLoading(true)
            } else {
                setIsLoading(false)
            }
        }

    }, [fetchStatus]);

    const save = () =>{
        const formData = new FormData();

        const form = {
            name: state.name ?? "",
            mandatory_subtype: state.mandatory_subtype ?? false,
            mandatory_quantity: state.mandatory_quantity ?? false,
            subtype: JSON.stringify(subtypeState)
        };

        Object.keys(form).forEach(key => {
            formData.append(key, form[key])
        });

        if (!!id) formData.append('_method', 'PUT');

        setIsLoading(true);

        const response = !!id ? configObjectService.edit(id, formData) : configObjectService.create(formData);

        response.then(({data}) => {
            projectUtils.success(!id);
            if (id) {
                refetch();
                return;
            }

            navigateToEdit(data)
            })
            .catch((error) => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = AlertMessages[message] ?? message;
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setIsLoading(false));

    }

    const navigateToEdit = (data) => {
        let urlDev = `/objects/form/${data.id}`;
        navigate(urlDev);
    }

    const destroy = () => {
        Swal.fire({
            title: "Excluir",
            text: `Excluir o objeto "${state.name ? state.name : state.alias}"?`,
            icon: "warning",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);

                configObjectService.destroy(id)
                    .then(() => {
                        navigateToList()
                        projectUtils.success(true, "Excluído com sucesso");
                    })
                    .catch((error) => {
                        console.error(error);

                        const {message} = error.response.data;

                        let errMessage = AlertMessages[message] ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    const navigateToList = () => {
        let urlDev = `/objects`;
        navigate(urlDev);
    }

    const addLine = () => {
        if (coin){
            setSubtypeState([...subtypeState, {id: uuidv4(), name: "", measure: [{value: "UNITY", label: "Unidade (un)"}]}]);

        }else{
            setSubtypeState([...subtypeState, {id: uuidv4(), name: "", measure: []}]);
        }
    };

    const removeLine = (id) => {
        setSubtypeState(subtypeState.filter((line) => line.id !== id));
    };

    const updateSubtype = (id, novoValor) => {
        setSubtypeState((prev) =>
            prev.map((line) =>
                line.id === id ? { ...line, name: novoValor } : line
            )
        );
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            save()
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [state, subtypeState]);

    const newNature = () => {
        let urlDev = `/objects/form`;
        navigate(urlDev);
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Objeto
                </Template.Title>
                {
                    id &&
                    <>
                        <Button target={"primary-outline"} onClick={() => newNature()}>
                            <FaPlus/>
                            Adicionar objeto
                        </Button>
                    </>
                }
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Header>
                        <Card.Title>Tipo</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-12">
                                <InputForm id="name" value={state.name} label="Nome *" onChange={onChange} disabled={isSystem} />
                            </div>

                            <div className="col-span-4">
                                <InputCheckbox id="mandatory_subtype" checked={state.mandatory_subtype}
                                               label="Preenchimento obrigatório do subtipo"
                                               onChange={handleCheckBox}
                                               disabled={isSystem} />
                            </div>
                            <div className="col-span-8">
                                <InputCheckbox id="mandatory_quantity" checked={state.mandatory_quantity}
                                               label="Preenchimento obrigatório da quantidade"
                                               onChange={handleCheckBox}
                                               disabled={isSystem}/>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className={"mt-2"}>
                    <Card.Header className={"gap-2"}>
                        <Card.Title>Subtipo</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {subtypeState.map((sub) => (
                            <>
                                <div className="grid grid-cols-12 gap-4 mt-3" key={sub.id} >
                                    <div className="col-span-8">
                                        <InputForm
                                            id={`subtype-` + sub.id}
                                            value={sub.name}
                                            onChange={(e) => updateSubtype(sub.id, e.target.value)}
                                            label="Nome"
                                            disabled={isSystem}
                                        />
                                    </div>

                                    <div className="flex items-end gap-2 col-span-4">
                                        <div className="flex-1 min-w-0">
                                            <SelectComponent
                                                isDisabled={isSystem}
                                                value={sub.measure}
                                                options={Measure}
                                                label={"Unidades de medida"}
                                                onChange={(selected) => {
                                                    setSubtypeState((prev) =>
                                                        prev.map((measureSelected) =>
                                                            measureSelected.id === sub.id ? { ...measureSelected, measure: selected } : measureSelected
                                                        )
                                                    );
                                                }}
                                                isMulti
                                            />
                                        </div>


                                        {
                                            !isSystem &&
                                            <div className="flex shrink-0">
                                                <Button target={"danger"} onClick={() => removeLine(sub.id)}>
                                                    <FaTrash/>
                                                </Button>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </>
                        ))}
                        <div className={"mt-3"}>
                            <Button onClick={() => addLine()} disabled={isSystem}>
                                <FaPlus/>
                                Adicionar
                            </Button>
                        </div>
                    </Card.Body>
                </Card>

                <Card className={"mt-2"}>
                    <Card.Body>
                        {
                            isSystem ? (
                                <div className="m-2">
                                    <div className="w-fit flex items-center p-2 text-[#1c657d] border border-blue-300 rounded-lg bg-[#A7D4FF]">
                                        <FaInfoCircle className="flex-shrink-0 w-4 h-4 mr-2" />
                                        <span className="text-sm font-medium">
                                            Esse é um objeto padrão de sistema e não pode ser editado ou excluído
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full flex justify-end ">
                                    <div className="flex gap-2">
                                        {
                                            id &&
                                            <>
                                                <Button target="danger" onClick={destroy} actionFeedbackType='delete'>

                                                    Excluir
                                                </Button>
                                            </>
                                        }

                                        <Button onClick={save} actionFeedbackType='save'>

                                            Salvar
                                        </Button>
                                    </div>
                                </div>
                            )
                        }
                    </Card.Body>
                </Card>
            </Template.Body>

        </Template>
    );
};

export default ConfigObjectForm;